/**
 * useTour — the single public API for the guided tour system.
 *
 * Internals are split across:
 *   tour/tourTypes.ts      — shared types & constants
 *   tour/tourWaitUtils.ts  — async DOM utilities
 *   tour/tourUiUtils.ts    — celebration popup helpers
 *   tour/tourStepDefs.ts   — all step arrays
 */
import { useCallback, useRef, useState } from "react";
import { driver, type Config } from "driver.js";
import "driver.js/dist/driver.css";
import { useNavigate } from "react-router-dom";

import {
  TOUR_VERSION,
  TOUR_COMPLETED_KEY,
  TOUR_SEEN_VERSION_KEY,
  NEWSLETTER_PROMPTED_KEY,
  CV_SAVED_EVENT,
  CV_DOWNLOAD_CONFIRMED_EVENT,
  getUserKey,
  type TourStep,
} from "./tour/tourTypes";
import {
  queryVisible,
  waitForElement,
  waitForAnyElement,
  waitForInputValue,
  waitForClick,
  waitForClickTarget,
  waitForWindowEvent,
  waitForRatingOutcome,
} from "./tour/tourWaitUtils";
import { clearCelebrationPopups, showCelebrationPopup } from "./tour/tourUiUtils";
import {
  getDesktopSteps,
  getMobileSteps,
  getJourneyIntroSteps,
  getBuilderChoiceInstructionSteps,
  getTemplateHintSteps,
  getUploadPathInstructionSteps,
  getEditorInstructionSteps,
  getRatingLauncherStep,
  getRatingPopupSteps,
  getFeaturePageTours,
} from "./tour/tourStepDefs";

export function useTour(isAdmin: boolean = false, userId?: string) {
  const [isTourActive, setIsTourActive] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [showNewsletterPrompt, setShowNewsletterPrompt] = useState(false);
  const navigate = useNavigate();
  const activeDriverRef = useRef<ReturnType<typeof driver> | null>(null);

  // ─── localStorage helpers ────────────────────────────────────────────────

  // Both markers hold the tour version, so bumping TOUR_VERSION re-offers the
  // tour to everyone once while leaving the old markers harmlessly in place.
  const isTourCompleted = useCallback(() => {
    const key = getUserKey(TOUR_COMPLETED_KEY, userId);
    return localStorage.getItem(key) === TOUR_VERSION;
  }, [userId]);

  const markTourCompleted = useCallback(() => {
    const key = getUserKey(TOUR_COMPLETED_KEY, userId);
    localStorage.setItem(key, TOUR_VERSION);
  }, [userId]);

  const hasSeenCurrentTourVersion = useCallback(() => {
    const key = getUserKey(TOUR_SEEN_VERSION_KEY, userId);
    return localStorage.getItem(key) === TOUR_VERSION;
  }, [userId]);

  const markTourVersionSeen = useCallback(() => {
    const key = getUserKey(TOUR_SEEN_VERSION_KEY, userId);
    localStorage.setItem(key, TOUR_VERSION);
  }, [userId]);

  const isNewsletterPrompted = useCallback(() => {
    const key = getUserKey(NEWSLETTER_PROMPTED_KEY, userId);
    return localStorage.getItem(key) === "true";
  }, [userId]);

  const markNewsletterPrompted = useCallback(() => {
    const key = getUserKey(NEWSLETTER_PROMPTED_KEY, userId);
    localStorage.setItem(key, "true");
  }, [userId]);

  const resetTour = useCallback(() => {
    const key = getUserKey(TOUR_COMPLETED_KEY, userId);
    localStorage.removeItem(key);
  }, [userId]);

  // ─── Driver helpers ──────────────────────────────────────────────────────

  // Must match the builder's own split: its toolbar/header trees switch at
  // Tailwind `lg`, so anything narrower is driven with the mobile selectors.
  const isMobile = useCallback(() => window.innerWidth < 1024, []);

  const destroyActiveDriver = useCallback(() => {
    if (activeDriverRef.current) {
      activeDriverRef.current.destroy();
      activeDriverRef.current = null;
    }
  }, []);

  const runTourPhase = useCallback(
    (
      steps: TourStep[],
      options?: {
        doneBtnText?: string;
        progressText?: string;
      },
    ) => {
      return new Promise<boolean>((resolve) => {
        let driverObj: ReturnType<typeof driver> | null = null;
        // Keep any step whose target exists, but resolve it lazily at
        // highlight time, preferring the *visible* match. The builder keeps
        // hidden responsive twins of most targets in the DOM (a step pointed
        // at one renders its popover on a zero-size box), and pages like
        // Profile reveal a later step's target only after an earlier step
        // switches tabs — so neither "first match" nor "visible right now"
        // is the right answer up front.
        const availableSteps = steps.flatMap<TourStep>((step) => {
          if (!step.element || typeof step.element !== "string") return [step];
          const selector = step.element;
          if (!document.querySelector(selector)) return [];
          return [
            {
              ...step,
              element: () =>
                (queryVisible(selector) ??
                  document.querySelector(selector)) as Element,
            },
          ];
        });

        if (availableSteps.length === 0) {
          resolve(true);
          return;
        }

        let closedByUser = false;

        const driverConfig: Config = {
          showProgress: true,
          showButtons: ["next", "previous", "close"],
          steps: availableSteps,
          nextBtnText: "Next →",
          prevBtnText: "← Back",
          doneBtnText: options?.doneBtnText ?? "Continue",
          progressText: options?.progressText ?? "{{current}} of {{total}}",
          popoverClass: "resumex-tour-popover",
          disableActiveInteraction: false,
          onCloseClick: () => {
            const shouldCancelTour = window.confirm(
              "This tour is not complete yet. Click OK to cancel or Cancel to continue.",
            );

            if (!shouldCancelTour) {
              return;
            }

            closedByUser = true;
            driverObj?.destroy();
          },
          onDestroyed: () => {
            activeDriverRef.current = null;
            resolve(!closedByUser);
          },
        };

        driverObj = driver(driverConfig);
        activeDriverRef.current = driverObj;
        driverObj.drive();
      });
    },
    [],
  );

  // ─── Feature page tour sequence ──────────────────────────────────────────

  const runFeatureTourSequence = useCallback(async () => {
    const featurePageTours = getFeaturePageTours();
    for (let i = 0; i < featurePageTours.length; i += 1) {
      const pageTour = featurePageTours[i];
      navigate(pageTour.path);
      const pageReady = await waitForElement(pageTour.waitFor, 20000);
      if (!pageReady) {
        return false;
      }

      const steps =
        typeof pageTour.steps === "function"
          ? pageTour.steps()
          : pageTour.steps;
      const phaseOk = await runTourPhase(steps, {
        doneBtnText: i === featurePageTours.length - 1 ? "Finish" : "Next Page",
      });
      if (!phaseOk) {
        return false;
      }
    }

    const removeSecondCelebration = showCelebrationPopup(
      "Feature Tour Completed",
      "You explored all key pages. You are fully ready to use ResumeX confidently.",
      "🎊 🌟 🎊",
    );
    await waitForElement('[data-tour="tour-celebration-popup"]', 2000);
    await runTourPhase(
      [
        {
          element: '[data-tour="tour-celebration-popup"]',
          popover: {
            title: "All Done",
            description: "You completed both journeys successfully.",
            side: "top",
            align: "center",
          },
        },
      ],
      { doneBtnText: "Finish 🎉" },
    );
    removeSecondCelebration();

    return true;
  }, [navigate, runTourPhase]);

  // ─── Main tour orchestrator ──────────────────────────────────────────────

  const startTour = useCallback(
    async (mode: "full" | "journey" | "features" = "full") => {
      destroyActiveDriver();
      setIsTourActive(true);

      const stopTour = (completed: boolean) => {
        destroyActiveDriver();
        clearCelebrationPopups();
        setIsTourActive(false);
        if (completed) {
          markTourCompleted();
          if (!isAdmin && !isNewsletterPrompted()) {
            setShowNewsletterPrompt(true);
          }
        }
      };

      try {
        const mobile = isMobile();

        if (mode === "features") {
          const featureOnlyFinished = await runFeatureTourSequence();
          stopTour(featureOnlyFinished);
          return;
        }

        if (isAdmin) {
          const finished = await runTourPhase(
            mobile ? getMobileSteps(true) : getDesktopSteps(true),
            { doneBtnText: "Finish 🎉" },
          );
          stopTour(finished);
          return;
        }

        const introFinished = await runTourPhase(getJourneyIntroSteps(mobile), {
          doneBtnText: "Open Builder",
        });
        if (!introFinished) {
          stopTour(true);
          return;
        }

        navigate("/editor");
        const selectorReady = await waitForElement(
          '[data-tour="builder-selector-title"]',
          15000,
        );
        if (!selectorReady) {
          stopTour(false);
          return;
        }

        const choiceIntroFinished = await runTourPhase(
          getBuilderChoiceInstructionSteps(),
          { doneBtnText: "I Will Choose" },
        );
        if (!choiceIntroFinished) {
          stopTour(true);
          return;
        }

        // The selector has two real outcomes: the upload dropzone or the
        // design gallery. Either way we end up waiting for the editor.
        const choiceDetected = await waitForAnyElement(
          [
            '[data-tour="builder-upload-dropzone"]',
            '[data-tour="builder-template-card"]',
          ],
          600000,
        );

        if (!choiceDetected) {
          stopTour(false);
          return;
        }

        if (choiceDetected === '[data-tour="builder-upload-dropzone"]') {
          const uploadPhaseFinished = await runTourPhase(
            getUploadPathInstructionSteps(),
            { doneBtnText: "I Will Upload" },
          );
          if (!uploadPhaseFinished) {
            stopTour(true);
            return;
          }
        } else {
          const templateHintFinished = await runTourPhase(
            getTemplateHintSteps(),
            { doneBtnText: "I Will Choose" },
          );
          if (!templateHintFinished) {
            stopTour(true);
            return;
          }

          // No click gate here on purpose: every card shares this selector,
          // including its "Preview" button, which opens a modal rather than
          // choosing a design. That click satisfied this wait without a
          // template ever being picked, so the tour moved on believing a
          // design was chosen while the editor never opened — which read as
          // "I selected a design and nothing showed up." The only real
          // signal that a design was picked is the editor actually opening,
          // which the wait below already covers.
        }

        // Upload parsing can take a while, so both paths share the long wait.
        const editorOpened = await waitForElement(
          '[data-tour="builder-editor-title"]',
          600000,
        );
        if (!editorOpened) {
          stopTour(false);
          return;
        }

        const editorPhaseFinished = await runTourPhase(
          getEditorInstructionSteps(mobile),
          { doneBtnText: "I Will Do This" },
        );
        if (!editorPhaseFinished) {
          stopTour(true);
          return;
        }

        const enteredName = await waitForInputValue(
          '[data-tour="builder-full-name"]',
          (value) => value.trim().length > 0,
          600000,
        );
        if (!enteredName) {
          stopTour(false);
          return;
        }

        const saveSelector = mobile
          ? '[data-tour="builder-save-btn-mobile"]'
          : '[data-tour="builder-save-btn"]';
        const previewSelector = mobile
          ? '[data-tour="builder-preview-btn-mobile"]'
          : '[data-tour="builder-preview-btn"]';
        const downloadSelector = mobile
          ? '[data-tour="builder-download-btn-mobile"]'
          : '[data-tour="builder-download-btn"]';
        const saveDetectedPromise = waitForWindowEvent(CV_SAVED_EVENT, 600000);
        // Same three either way now: on desktop these sit in the app header
        // rather than behind a dock the user had to open first.
        const firstClickSelectors = [
          saveSelector,
          previewSelector,
          downloadSelector,
        ];

        const firstClicked = await waitForClickTarget(
          firstClickSelectors,
          600000,
        );
        if (!firstClicked) {
          stopTour(false);
          return;
        }

        // Previously the desktop first click was "open the dock", so a second
        // click had to be awaited for the real action. The action buttons are
        // in the header now, so the first click already is it.
        const clickedSaveRelatedAction = firstClicked;

        const saveDetected = await saveDetectedPromise;
        if (!saveDetected) {
          stopTour(false);
          return;
        }

        if (
          clickedSaveRelatedAction === previewSelector ||
          clickedSaveRelatedAction === downloadSelector
        ) {
          // Best effort only. The save is already confirmed; this just holds
          // the celebration back while a PDF is generating. Preview and
          // download both route through the plan-gated export now, so a free
          // user lands on the paywall instead of ever confirming a download —
          // that must not strand the tour.
          await Promise.race([
            waitForWindowEvent(CV_DOWNLOAD_CONFIRMED_EVENT, 120000),
            waitForElement('[data-tour="paywall-modal"]', 120000),
          ]);
        }

        await waitForAnyElement(
          ['[data-toast-type="success"]', '[data-tour="rating-launcher"]'],
          8000,
        );

        const removeFirstCelebration = showCelebrationPopup(
          "Resume Saved",
          "Great work. You completed the real creation and save flow.",
        );
        try {
          await waitForElement('[data-tour="tour-celebration-popup"]', 2000);
          await runTourPhase(
            [
              {
                element: '[data-tour="tour-celebration-popup"]',
                popover: {
                  title: "Celebration",
                  description: "Nice. Now please submit your real rating.",
                  side: "top",
                  align: "center",
                },
              },
            ],
            { doneBtnText: "Continue" },
          );
        } finally {
          removeFirstCelebration();
          clearCelebrationPopups();
        }

        // The launcher only exists for users who haven't rated yet. Anyone
        // who already has skips straight past this section instead of the
        // tour waiting on a button that will never appear.
        let popupOpened = Boolean(
          document.querySelector('[data-tour="rating-popup"]'),
        );
        const launcherEl = queryVisible(
          '[data-tour="rating-launcher"]',
        ) as HTMLButtonElement | null;

        if (popupOpened || launcherEl) {
          if (!popupOpened && launcherEl) {
            const ratingLauncherPhase = await runTourPhase(
              getRatingLauncherStep(),
              { doneBtnText: "Next ->" },
            );
            if (!ratingLauncherPhase) {
              stopTour(true);
              return;
            }

            launcherEl.click();
            popupOpened = await waitForElement(
              '[data-tour="rating-popup"]',
              4000,
            );
          }

          if (!popupOpened) {
            const clickedLauncher = await waitForClick(
              '[data-tour="rating-launcher"]',
              600000,
            );
            if (!clickedLauncher) {
              stopTour(false);
              return;
            }

            popupOpened = await waitForElement(
              '[data-tour="rating-popup"]',
              120000,
            );
          }

          if (!popupOpened) {
            stopTour(false);
            return;
          }

          clearCelebrationPopups();

          const ratingPopupPhase = await runTourPhase(getRatingPopupSteps(), {
            doneBtnText: "Submit Now",
          });
          if (!ratingPopupPhase) {
            stopTour(true);
            return;
          }

          const ratingOutcome = await waitForRatingOutcome(600000);
          if (!ratingOutcome) {
            stopTour(false);
            return;
          }
        }

        clearCelebrationPopups();

        if (mode === "journey") {
          stopTour(true);
          return;
        }

        const featureFinished = await runFeatureTourSequence();
        stopTour(featureFinished);
      } catch (err) {
        console.error("Tour failed:", err);
        stopTour(false);
      }
    },
    [
      destroyActiveDriver,
      isAdmin,
      isMobile,
      isNewsletterPrompted,
      markTourCompleted,
      navigate,
      runFeatureTourSequence,
      runTourPhase,
    ],
  );

  // ─── Convenience wrappers ────────────────────────────────────────────────

  const startJourneyTour = useCallback(async () => {
    await startTour("journey");
  }, [startTour]);

  const startFeatureTour = useCallback(async () => {
    await startTour("features");
  }, [startTour]);

  /**
   * Contextual mini-tour for the help button when it's clicked *inside* the
   * CV builder. `startTour()` always restarts the whole create-a-resume
   * journey from /home — right, for someone who hasn't built anything yet,
   * but wrong for someone already sitting in the editor: it drags them away
   * from their in-progress CV to relive the template picker. This instead
   * walks whatever the builder is actually showing right now, in place.
   */
  const startBuilderTour = useCallback(async () => {
    if (isTourActive) return;
    destroyActiveDriver();
    setIsTourActive(true);
    try {
      const mobile = isMobile();
      if (document.querySelector('[data-tour="builder-editor-title"]')) {
        await runTourPhase(getEditorInstructionSteps(mobile), {
          doneBtnText: "Got it",
        });
      } else if (document.querySelector('[data-tour="builder-selector-title"]')) {
        await runTourPhase(getBuilderChoiceInstructionSteps(), {
          doneBtnText: "Got it",
        });
      }
    } finally {
      destroyActiveDriver();
      setIsTourActive(false);
    }
  }, [destroyActiveDriver, isMobile, isTourActive, runTourPhase]);

  /**
   * Offer the tour once per user per TOUR_VERSION: new users get it on their
   * first sign-in, and every existing user gets it once after a release that
   * bumps the version. Marked as seen the moment it's offered, so declining,
   * closing the tab, or bailing mid-tour never re-triggers the greeting —
   * the Profile page still lets anyone restart it by hand.
   */
  const showFirstTimeGreeting = useCallback(() => {
    if (!userId) return;
    if (hasSeenCurrentTourVersion() || isTourCompleted()) return;
    markTourVersionSeen();
    setIsFirstTimeUser(true);
    setShowGreeting(true);
  }, [hasSeenCurrentTourVersion, isTourCompleted, markTourVersionSeen, userId]);

  const handleGreetingClose = useCallback(
    (startTourAfter: boolean = true) => {
      setShowGreeting(false);
      if (startTourAfter) {
        setTimeout(() => {
          void startTour();
        }, 300);
      }
    },
    [startTour],
  );

  const handleSkipTour = useCallback(() => {
    setShowGreeting(false);
    markTourCompleted();
  }, [markTourCompleted]);

  const dismissNewsletterPrompt = useCallback(() => {
    setShowNewsletterPrompt(false);
    markNewsletterPrompted();
  }, [markNewsletterPrompted]);

  return {
    isTourActive,
    showGreeting,
    isFirstTimeUser,
    isTourCompleted: isTourCompleted(),
    showNewsletterPrompt,
    startTour,
    startJourneyTour,
    startFeatureTour,
    startBuilderTour,
    resetTour,
    showFirstTimeGreeting,
    handleGreetingClose,
    handleSkipTour,
    markTourCompleted,
    dismissNewsletterPrompt,
  };
}
